import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import type {
  ChangeDirection,
  MetricComparison,
  MetricUnavailableReason,
  PerformancePageOverview,
} from 'api/new-analytics/types';
import NumberUtil from 'utils/number';
import TimeUtils from 'app/utils/time';

export interface PerformanceMetricDisplay {
  value: string;
  badge?: number;
  previousValueDisplay?: string;
  changeDirection?: ChangeDirection;
  coverage?: {
    range: string;
    daysCovered?: number;
    daysRequested?: number;
  };
  unavailableReason?: MetricUnavailableReason;
  availableFrom?: string;
}

const coalesceNumber = NumberUtil.coalesceFinite;

/** Placeholder shown when a metric has no usable value ("not available"). */
export const NOT_AVAILABLE = '—';

function formatCoveredRange(since: string, until: string): string {
  const start = dayjs(since);
  const end = dayjs(until);

  if (!start.isValid() || !end.isValid()) {
    return '';
  }
  if (start.isSame(end, 'day')) {
    return start.format('D.M.YYYY');
  }
  if (start.isSame(end, 'year')) {
    return `${start.format('D.M.')} - ${end.format('D.M.YYYY')}`;
  }
  return `${start.format('D.M.YYYY')} - ${end.format('D.M.YYYY')}`;
}

function coverageFromMetric(metric: MetricComparison | undefined) {
  if (!metric?.effective_since || !metric.effective_until) {
    return undefined;
  }
  return {
    range: formatCoveredRange(metric.effective_since, metric.effective_until),
    daysCovered: metric.days_covered,
    daysRequested: metric.days_requested,
  };
}

/**
 * A snapshot describes the account now rather than the window — a follower total reads
 * the same whichever dates are asked for. The endpoint still sends the comparison
 * fields, and both windows resolve to the same stored row, so a tile left to render
 * them shows a permanent "0 %". This is what keeps them off it.
 */
function comparableMetric(metric: MetricComparison | undefined) {
  return metric?.is_snapshot === true ? undefined : metric;
}

function badgeFromMetric(metric: MetricComparison | undefined) {
  if (!metric || metric.change_percent == null) {
    return undefined;
  }
  return coalesceNumber(metric.change_percent);
}

function directionFromMetric(
  metric: MetricComparison | undefined,
): ChangeDirection | undefined {
  return metric?.change_direction ?? undefined;
}

function previousCompactDisplay(metric: MetricComparison | undefined) {
  if (metric?.previous_value == null) {
    return undefined;
  }
  const pv = coalesceNumber(metric.previous_value);
  if (pv === undefined) {
    return undefined;
  }
  return NumberUtil.compact(pv);
}

function previousPercentDisplay(metric: MetricComparison | undefined) {
  if (metric?.previous_value == null) {
    return undefined;
  }
  const pv = coalesceNumber(metric.previous_value);
  if (pv === undefined) {
    return undefined;
  }
  return `${NumberUtil.format(pv, { maximumFractionDigits: 2 })}%`;
}

function previousWatchTimeDisplay(metric: MetricComparison | undefined) {
  if (metric?.previous_value == null) {
    return undefined;
  }
  const pv = coalesceNumber(metric.previous_value);
  if (pv === undefined) {
    return undefined;
  }
  return TimeUtils.formatMilliSeconds(pv);
}

export function getMetricUnavailableCaption(
  t: TFunction,
  display: PerformanceMetricDisplay,
): string | undefined {
  const reason = display.unavailableReason;

  if (reason === undefined) {
    return undefined;
  }

  if (reason === 'before_metric_launch' && display.availableFrom) {
    return t('analyticsPerformance.metrics.unavailable.beforeMetricLaunch', {
      date: dayjs(display.availableFrom).format('D.M.YYYY'),
    });
  }

  const keyByReason: Record<MetricUnavailableReason, string> = {
    range_exceeds_provider_limit: 'rangeExceedsProviderLimit',
    before_metric_launch: 'beforeMetricLaunchUnknownDate',
    beyond_provider_retention: 'beyondProviderRetention',
    not_provided_for_window: 'notProvidedForWindow',
    provider_error: 'providerError',
  };

  return t(`analyticsPerformance.metrics.unavailable.${keyByReason[reason]}`);
}

export function getMetricCoverageCaption(
  t: TFunction,
  display: PerformanceMetricDisplay,
): string | undefined {
  const coverage = display.coverage;

  if (!coverage?.range) {
    return undefined;
  }

  if (
    coverage.daysCovered !== undefined &&
    coverage.daysRequested !== undefined
  ) {
    return t('analyticsPerformance.metrics.coveredDays', {
      range: coverage.range,
      covered: coverage.daysCovered,
      requested: coverage.daysRequested,
    });
  }

  // Without the day counts the range alone reads as decoration — a reader sees dates
  // under a number and cannot tell they are the period the number actually describes.
  return t('analyticsPerformance.metrics.coveredRange', {
    range: coverage.range,
  });
}

export function getMetricComparisonCaption(
  t: TFunction,
  display: PerformanceMetricDisplay,
  isComparisonEnabled: boolean,
): string | undefined {
  const unavailable = getMetricUnavailableCaption(t, display);

  if (unavailable !== undefined) {
    return unavailable;
  }

  const coverage = getMetricCoverageCaption(t, display);

  if (coverage !== undefined) {
    return coverage;
  }

  if (!isComparisonEnabled || display.previousValueDisplay === undefined) {
    return undefined;
  }
  return t('analyticsPerformance.metrics.valueInPreviousPeriod', {
    value: display.previousValueDisplay,
  });
}

export function getMetricDisplay(
  overview: PerformancePageOverview | undefined,
  key: string,
): PerformanceMetricDisplay {
  const metric = overview?.[key];
  const value = coalesceNumber(metric?.value);
  if (value === undefined) {
    return {
      value: NOT_AVAILABLE,
      unavailableReason: metric?.unavailable_reason,
      availableFrom: metric?.available_from,
    };
  }
  const comparable = comparableMetric(metric);

  return {
    value: NumberUtil.compact(value),
    badge: badgeFromMetric(comparable),
    previousValueDisplay: previousCompactDisplay(comparable),
    changeDirection: directionFromMetric(comparable),
    coverage: coverageFromMetric(metric),
  };
}

export function getPercentMetricDisplay(
  overview: PerformancePageOverview | undefined,
  key: string,
): PerformanceMetricDisplay {
  const metric = overview?.[key];
  const value = coalesceNumber(metric?.value);
  if (value === undefined) {
    return { value: NOT_AVAILABLE };
  }

  return {
    value: `${NumberUtil.format(value, { maximumFractionDigits: 2 })}%`,
    badge: badgeFromMetric(metric),
    previousValueDisplay: previousPercentDisplay(metric),
    changeDirection: directionFromMetric(metric),
  };
}

export function getMetricNumericValue(
  overview: PerformancePageOverview | undefined,
  key: string,
): number {
  const metric = overview?.[key];
  return coalesceNumber(metric?.value) ?? 0;
}

export function getWatchTimeDisplay(
  overview: PerformancePageOverview | undefined,
): PerformanceMetricDisplay {
  const metric = overview?.video_time_watched;
  const ms = coalesceNumber(metric?.value);
  if (ms === undefined) {
    return { value: NOT_AVAILABLE };
  }
  return {
    value: TimeUtils.formatMilliSeconds(ms),
    badge: badgeFromMetric(metric),
    previousValueDisplay: previousWatchTimeDisplay(metric),
    changeDirection: directionFromMetric(metric),
  };
}

export function getContentMetricDisplay(
  value: number | undefined,
): PerformanceMetricDisplay {
  const coalesced = coalesceNumber(value);
  if (coalesced === undefined) {
    return { value: NOT_AVAILABLE };
  }
  return {
    value: NumberUtil.compact(coalesced),
  };
}

export function getContentPercentMetricDisplay(
  value: number | undefined,
): PerformanceMetricDisplay {
  const coalesced = coalesceNumber(value);
  if (coalesced === undefined) {
    return { value: NOT_AVAILABLE };
  }
  return {
    value: `${NumberUtil.format(coalesced, { maximumFractionDigits: 2 })}%`,
  };
}

export function getContentWatchTimeDisplay(
  ms: number | undefined,
): PerformanceMetricDisplay {
  const coalesced = coalesceNumber(ms);
  if (coalesced === undefined) {
    return { value: NOT_AVAILABLE };
  }
  return {
    value: TimeUtils.formatMilliSeconds(coalesced),
  };
}

export function getComputedRateDisplay(
  overview: PerformancePageOverview | undefined,
  numeratorKey: string,
  denominatorKey: string,
): PerformanceMetricDisplay {
  const numMetric = overview?.[numeratorKey];
  const denMetric = overview?.[denominatorKey];
  const num = coalesceNumber(numMetric?.value);
  const den = coalesceNumber(denMetric?.value);
  if (num === undefined || den === undefined || den === 0) {
    return { value: NOT_AVAILABLE };
  }
  const pct = (num / den) * 100;
  const prevNum = coalesceNumber(numMetric?.previous_value);
  const prevDen = coalesceNumber(denMetric?.previous_value);
  const previousValueDisplay =
    prevNum !== undefined && prevDen !== undefined && prevDen !== 0
      ? `${NumberUtil.format((prevNum / prevDen) * 100, {
          maximumFractionDigits: 2,
        })}%`
      : undefined;
  return {
    value: `${NumberUtil.format(pct, { maximumFractionDigits: 2 })}%`,
    previousValueDisplay,
  };
}
